import { type ReactNode, useMemo } from "react";
import { Typography, type TypographyProps, styled } from "~/common/mui";
import { FixedWidthTileCarousel } from "~/common/components/carousel/FixedWidthTileCarousel";
import { useIsMinDesktopSizeL, useIsMinDesktopSizeM } from "~/common/hooks/useMediaQuery";
import {
  ProductRecommendationsTile,
  type trackingAttributesProps,
} from "~/features/saleRecommendations/components/ProductRecommendationTile";
import { type ProductData } from "~/domains/product/types/ProductData";
import type { ProductDescriptor } from "~/domains/product/types/ProductDescriptor";
import { ProductRecommendationTileSkeleton } from "~/features/saleRecommendations/components/ProductRecommendationTileSkeleton";
import {
  CONTAINER_DESKTOP_L,
  CONTAINER_DESKTOP_M,
  CONTAINER_DESKTOP_S,
} from "~/common/utils/screenSizes";

export enum ProductRecoType {
  topSeller = "top-seller",
  recentlyViewed = "recently-viewed",
  campaignSRP = "campaign-SRP",
  campaignPDP = "campaign-PDP",
  fccGap = "fcc",
}
interface Props {
  title: ReactNode;
  loading?: boolean;
  loadingLabel: string;
  className?: string;
  products: string[];
  variant: string;
  getTrackingAttributes: (
    product: ProductData,
    productDescriptor: ProductDescriptor,
    isPMA: boolean,
  ) => trackingAttributesProps;
}

export const ProductRecommendationSection = ({
  title,
  loading,
  loadingLabel,
  className,
  products,
  variant,
  ...rest
}: Props) => {
  const isDesktopL = useIsMinDesktopSizeL();
  const isDesktopM = useIsMinDesktopSizeM();

  const isSectionSmall = variant === ProductRecoType.topSeller || variant === ProductRecoType.campaignSRP;

  const numItems = useMemo(() => {
    let gap;
    let width;
    if (isSectionSmall) {
      gap = 16;
      width = 242;
    } else if (isDesktopL || isDesktopM) {
      gap = 32;
      width = 342;
    } else {
      gap = 16;
      width = 242;
    }
    return { gap, width };
  }, [window.innerWidth]);

  let items;
  let renderItem;
  let hideScrollButtons;
  if (loading) {
    items = ["", "", "", ""];
    renderItem = (productCode: string) => <ProductRecommendationTileSkeleton itemWidth={numItems.width} />;

    hideScrollButtons = true;
  } else {
    items = products;
    renderItem = (productCode: string) => (
      <ProductRecommendationsTile
        key={productCode}
        productCode={productCode}
        squeezePrice={true}
        itemWidth={numItems.width}
        variant={variant}
        {...rest}
      />
    );
    hideScrollButtons = false;
  }

  return (
    <SectionWrapper
      className={className}
      variant={isSectionSmall ? "small" : "fullbleed"}
    >
      {title}

      <FixedWidthTileCarousel
        gap={numItems.gap}
        itemWidth={numItems.width}
        items={items}
        hideButtons={hideScrollButtons}
      >
        {renderItem}
      </FixedWidthTileCarousel>
    </SectionWrapper>
  );
};

export const ProductRecommendationTitle = (props: TypographyProps) => (
  <Typography {...props} sx={{ color: "text.primary", pb: 2 }} variant={"headlineSmall"} />
);

const SectionWrapper = styled("div", {
  shouldForwardProp: (prop) => prop !== "variant",
})<{ variant: "small" | "fullbleed" }>(({ theme, variant }) =>
  variant === "small"
    ? {
        display: "flex",
        flexDirection: "column",
        "--arrow-offset": "-20px",
        padding: theme.spacing(3),
        background: theme.palette.surface.main,
        borderRadius: "12px",
      }
    : {
        padding: "24px",
        "--arrow-offset": "-20px",
        "--right-arrow-offset": "-20px",
        background: theme.palette.surface.main,
        width: "100vw",
        marginTop: theme.spacing(4),
        marginLeft: "-16px",
        boxSizing: "border-box",
        [theme.breakpoints.up("tablet")]: {
          "--column-amount": "3",
          marginLeft: "-24px",
          padding: "16px 12px",
        },
        [theme.breakpoints.up("desktop-s")]: {
          "--column-amount": "4",
          marginLeft: `calc((${CONTAINER_DESKTOP_S}px - 100vw) / 2)`,
          padding: `32px calc((100vw - ${CONTAINER_DESKTOP_S}px) / 2)`,
        },
        [theme.breakpoints.up("desktop-m")]: {
          "--right-arrow-offset": "0px",
          "--column-amount": "4",
          marginLeft: `calc((${CONTAINER_DESKTOP_M}px - 100vw) / 2)`,
          padding: `32px calc((100vw - ${CONTAINER_DESKTOP_M}px) / 2)`,
        },
        [theme.breakpoints.up("desktop-l")]: {
          "--column-amount": "4",
          marginLeft: `calc((${CONTAINER_DESKTOP_L}px - 100vw) / 2)`,
          padding: `32px calc((100vw - ${CONTAINER_DESKTOP_L}px) / 2)`,
        },
      },
);
